import React from "react";
import { Alert, Button, Col, Divider, Row, Typography, message } from "antd";
import { ApiOutlined, CopyOutlined, KeyOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            message.success("Copied to clipboard");
        } catch {
            message.error("Could not copy to clipboard");
        }
    };

    return (
        <div className="code-block">
            <Button className="code-block-copy" size="small" icon={<CopyOutlined />} onClick={copy}>
                Copy
            </Button>
            <pre>
                <code>{code}</code>
            </pre>
        </div>
    );
};

const Docs: React.FC = () => {
    return (
        <div className="docs">
            <Row>
                <Col xs={{ span: 22, offset: 1 }} xl={{ span: 16, offset: 4 }}>
                    <Title>Using the Meteogen API</Title>
                    <Paragraph>
                        Meteogen produces high resolution wind &amp; solar power forecasts. Everything you can do in the dashboard - managing assets, parks and
                        portfolios, and retrieving forecasts - is also available through our REST API, so you can integrate our forecasts directly into your own
                        systems.
                    </Paragraph>
                    <Alert
                        style={{ marginBottom: 24 }}
                        type="info"
                        showIcon
                        icon={<ApiOutlined />}
                        message={
                            <span>
                                Looking for the full API reference? Every endpoint, request and response schema is documented in our interactive{" "}
                                <a target="_blank" rel="noopener noreferrer" href="/docs/index.html">
                                    Swagger API docs
                                </a>
                                , where you can also try out requests straight from your browser.
                            </span>
                        }
                    />
                    <Paragraph>
                        <Text strong>All dates, in both requests and responses, are in UTC</Text> (formatted like <Text code>2025-08-26T09:00:00Z</Text>). Make
                        sure to convert to/from your local timezone before sending or after reading dates.
                    </Paragraph>

                    <Divider />

                    <Title level={2}>1. Get an API key</Title>
                    <Paragraph>
                        API keys are created through the platform's GUI, in the{" "}
                        <Link to="/api-keys">
                            <KeyOutlined /> API Keys
                        </Link>{" "}
                        section. Give the key a name, choose whether it should be allowed to create/edit/delete assets, and copy the generated key -{" "}
                        <Text strong>it is only shown once</Text>.
                    </Paragraph>
                    <Paragraph>
                        Every request must include your API key in the <Text code>X-API-Key</Text> header:
                    </Paragraph>
                    <CodeBlock
                        code={`import requests

url = "https://app.meteogen.com/api/"
api_key = "your-api-key-here"

headers = {"X-API-Key": api_key, "Content-Type": "application/json"}`}
                    />

                    <Divider />

                    <Title level={2}>2. List your assets, parks &amp; portfolios</Title>
                    <Paragraph>
                        Assets are your individual PV arrays or wind turbines. Assets can optionally belong to a <Text strong>park</Text> (a physical site) and
                        to one or more <Text strong>portfolios</Text> (logical groupings, e.g. per client).
                    </Paragraph>
                    <CodeBlock
                        code={`assets = requests.get(url + "Assets", headers=headers).json()

for asset in assets:
    print(asset["id"], asset["name"], asset["capacity"], asset["type"], asset["parkId"])`}
                    />
                    <Paragraph>Parks and portfolios are listed the same way:</Paragraph>
                    <CodeBlock
                        code={`parks = requests.get(url + "Parks", headers=headers).json()
portfolios = requests.get(url + "Portfolios", headers=headers).json()`}
                    />

                    <Divider />

                    <Title level={2}>3. Create a PV or wind asset</Title>
                    <Paragraph>
                        New assets are created with a <Text code>POST</Text> to <Text code>Assets</Text>. A photovoltaic array needs its tilt/azimuth, a wind
                        turbine needs a power curve (wind speed / power pairs):
                    </Paragraph>
                    <CodeBlock
                        code={`pv_payload = {
    "name": "My new PV",
    "type": "PhotovoltaicArray",
    "capacity": 1000,
    "longitude": 23.0,
    "latitude": 38.3,
    "surfaceTilt": 25,
    "azimuthFrom": 180,
    "azimuthTo": 180,
    "bifacial": False
}

new_pv_asset = requests.post(url + "Assets", json=pv_payload, headers=headers).json()`}
                    />
                    <CodeBlock
                        code={`wind_payload = {
    "name": "My new Wind Turbine",
    "type": "WindTurbine",
    "capacity": 2000,
    "longitude": 23.0,
    "latitude": 38.3,
    "powerCurves": [
        {
            "label": "Default",
            "count": 1,
            "hubHeight": 80,
            # wind speed (m/s) and power (kW) pairs, one per line
            "powerCurveData": "0 0\\n1 100\\n2 400\\n3 900\\n4 1600\\n5 2500\\n10 3000\\n20 0"
        }
    ]
}

new_wind_asset = requests.post(url + "Assets", json=wind_payload, headers=headers).json()`}
                    />
                    <Paragraph>
                        Assets can be removed just as easily with <Text code>DELETE Assets/{"{id}"}</Text>:
                    </Paragraph>
                    <CodeBlock code={`requests.delete(url + f'Assets/{new_pv_asset["id"]}', headers=headers)`} />

                    <Divider />

                    <Title level={2}>4. Get a forecast</Title>
                    <Paragraph>
                        Forecasts can be requested per asset, per park or per portfolio, for a time range and an interval (<Text code>TenMinutes</Text>,{" "}
                        <Text code>FifteenMinutes</Text> or <Text code>Hour</Text>). Fetching forecasts for a park or portfolio is significantly faster than
                        fetching each asset individually.
                    </Paragraph>
                    <CodeBlock
                        code={`from datetime import datetime, timezone, timedelta

today = datetime.now(timezone.utc).replace(hour=0, minute=0, second=0, microsecond=0)
tomorrow = today + timedelta(days=1)
df = "%Y-%m-%dT%H:%M:%SZ"

payload = {
    "from": today.strftime(df),
    "to": tomorrow.strftime(df),
    "interval": "FifteenMinutes",
    "assetIds": [asset["id"] for asset in assets]
}

forecasts = requests.post(url + "Assets/Forecast", json=payload, headers=headers).json()

for asset_id in forecasts:
    for f in forecasts[asset_id]:
        print(asset_id, f["validDate"], f["power"], f["energy"])`}
                    />
                    <Paragraph>
                        Park and portfolio forecasts use the same payload (without <Text code>assetIds</Text>), posted to{" "}
                        <Text code>Parks/{"{parkId}"}/Forecast</Text> or <Text code>Portfolios/{"{portfolioId}"}/Forecast</Text> respectively.
                    </Paragraph>

                    <Divider />

                    <Title level={2}>5. Send observations &amp; control availability</Title>
                    <Paragraph>
                        If you have real-time SCADA or measurement data, posting it back to Meteogen lets our machine learning models correct the short term
                        forecast. Observations take <Text strong>power</Text> (not energy) in KW, so convert energy readings to power before sending them:
                    </Paragraph>
                    <Paragraph>
                        Observations are <Text strong>merged per field</Text>, not replaced wholesale: sending a new value for a field at a given timestamp only
                        overwrites that field. For example, if a timestamp already has a stored <Text code>power</Text> value and you later send only a{" "}
                        <Text code>windSpeed</Text> value for that same timestamp, the existing <Text code>power</Text> value is kept.
                    </Paragraph>
                    <CodeBlock
                        code={`observations = [
    {
        "assetId": new_pv_asset["id"],
        "date": "2025-08-26T09:00:00.000Z",
        "availability": 100,
        "irradiance": 200,
        "power": 100
    },
    {
        "assetId": new_pv_asset["id"],
        "date": "2025-08-26T10:00:00.000Z",
        "availability": 100,
        "irradiance": 250,
        "power": 120
    }
]

requests.post(url + "Assets/Observations", json=observations, headers=headers)`}
                    />
                    <Paragraph>
                        The same endpoint can be used to submit <Text strong>future</Text> observations to control an asset's expected availability - for
                        example to flag planned maintenance or curtailment. Just post an observation for a future date with the <Text code>availability</Text>{" "}
                        field set (0 to 100) and leave the other fields empty; a value of <Text code>0</Text> tells Meteogen the asset will be fully curtailed /
                        unavailable for that period.
                    </Paragraph>
                    <CodeBlock
                        code={`# Fully curtail the asset for one hour, starting in the future
future_curtailment = [
    {
        "assetId": new_pv_asset["id"],
        "date": "2026-09-01T12:00:00.000Z",
        "availability": 0
    }
]

requests.post(url + "Assets/Observations", json=future_curtailment, headers=headers)`}
                    />

                    <Divider />

                    <Paragraph>
                        That's everything you need to get going. For the complete list of endpoints, request/response schemas and to try requests live, head
                        over to the{" "}
                        <a target="_blank" rel="noopener noreferrer" href="/docs/index.html">
                            Swagger API docs
                        </a>
                        . Happy hacking, and feel free to reach out to <a href="mailto:info@meteogen.com">info@meteogen.com</a> with any questions.
                    </Paragraph>
                </Col>
            </Row>
        </div>
    );
};

export default Docs;
